import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { PersonSummary } from '../personnel/personnelTypes';
import type {
  ApplicantAssessment,
  ApplicantAssessmentAttachment,
  ApplicantAssessmentRequest,
  ApplicantAssessmentStageValue,
  ApplicantScoreWeights,
  ApplicantScoreWeightsRequest,
  ApplicantChecklistItem,
  ApplicantInput,
  ApplicantInterviewRequest,
  ApplicantListItem,
  ApplicantName,
  ApplicantRequirementListItem,
  ApplicantRequirementRequest,
  ApplicantScreening,
  ApplicantScreeningRequest,
  ApplicantStatusValue,
  JobPostingListItem,
} from './applicantTypes';

interface JobPostingQuery extends ListQuery {
  /** Comma-separated org-unit ids. */
  unit?: string;
}

interface ApplicantQuery extends Omit<ListQuery, 'status'> {
  includeDeleted?: boolean;
}

function toQueryString(query: JobPostingQuery & { includeDeleted?: boolean }): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  // Facets are sent whenever present, empty string included — an axis ticked to nothing matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.unit !== undefined) params.set('unit', query.unit);
  if (query.includeDeleted) params.set('includeDeleted', 'true');

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const applicantsApi = {
  listPostings: (query: JobPostingQuery, signal?: AbortSignal) =>
    api<PagedResult<JobPostingListItem>>(`/api/applicants/postings${toQueryString(query)}`, { signal }),

  getPosting: (postingId: string, signal?: AbortSignal) =>
    api<JobPostingListItem>(`/api/applicants/postings/${postingId}`, { signal }),

  list: (postingId: string, query: ApplicantQuery, signal?: AbortSignal) =>
    api<PagedResult<ApplicantListItem>>(`/api/applicants/postings/${postingId}/applicants${toQueryString(query)}`, {
      signal,
    }),

  create: (postingId: string, request: ApplicantInput) =>
    api<ApplicantListItem>(`/api/applicants/postings/${postingId}/applicants`, { method: 'POST', body: request }),

  get: (id: string, signal?: AbortSignal) => api<ApplicantListItem>(`/api/applicants/${id}`, { signal }),

  update: (id: string, request: ApplicantInput) =>
    api<ApplicantListItem>(`/api/applicants/${id}`, { method: 'PUT', body: request }),

  setStatus: (id: string, status: ApplicantStatusValue) =>
    api<ApplicantListItem>(`/api/applicants/${id}/status`, { method: 'POST', body: { status } }),

  /** Every place an interview is already set for — suggestions for the interview form's Place. */
  interviewPlaces: (signal?: AbortSignal) => api<string[]>('/api/applicants/interview-places', { signal }),

  /** The screening so far and the checklist — every live requirement, plus any deleted one already ticked. */
  screening: (id: string, signal?: AbortSignal) => api<ApplicantScreening>(`/api/applicants/${id}/screening`, { signal }),

  /** Records or corrects the Initial Screening. The server sets the status to match the outcome. */
  screen: (id: string, request: ApplicantScreeningRequest) =>
    api<ApplicantListItem>(`/api/applicants/${id}/screening`, { method: 'PUT', body: request }),

  /**
   * Files the applicant's document for one checklist line, replacing any earlier one. Only after the
   * screening has saved that line. Through `apiUpload` — multipart, real progress.
   */
  attachRequirementFile: (id: string, requirementId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<ApplicantChecklistItem>(`/api/applicants/${id}/requirements/${requirementId}/attachment`, form);
  },

  /**
   * Fetches a checklist line's file. A FETCH then an object URL, never an `<a href>` at the endpoint:
   * the token travels in a header no navigation sends.
   */
  downloadRequirementFile: (id: string, requirementId: string, signal?: AbortSignal) =>
    apiBlob(`/api/applicants/${id}/requirements/${requirementId}/attachment`, { signal }),

  /** Every assessment stage, in order. A health stage arrives `isHidden` without the medical key. */
  assessments: (id: string, signal?: AbortSignal) => api<ApplicantAssessment[]>(`/api/applicants/${id}/assessments`, { signal }),

  /** Records or corrects one stage — a Qualified applicant only. */
  recordAssessment: (id: string, stage: ApplicantAssessmentStageValue, request: ApplicantAssessmentRequest) =>
    api<ApplicantAssessment>(`/api/applicants/${id}/assessments/${stage}`, { method: 'PUT', body: request }),

  /** Files one document against a RECORDED stage. Multipart, real progress. */
  attachAssessmentFile: (
    id: string,
    stage: ApplicantAssessmentStageValue,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<ApplicantAssessmentAttachment>(`/api/applicants/${id}/assessments/${stage}/attachments`, form, {
      onProgress,
    });
  },

  /** Soft — the record and the bytes are kept. */
  removeAssessmentFile: (id: string, stage: ApplicantAssessmentStageValue, attachmentId: string) =>
    api<void>(`/api/applicants/${id}/assessments/${stage}/attachments/${attachmentId}`, { method: 'DELETE' }),

  /** A FETCH then an object URL, never an `<a href>` — the token travels in a header. */
  downloadAssessmentFile: (id: string, stage: ApplicantAssessmentStageValue, attachmentId: string, signal?: AbortSignal) =>
    apiBlob(`/api/applicants/${id}/assessments/${stage}/attachments/${attachmentId}`, { signal }),

  /** Score Points — read by every Summary. */
  scoreWeights: (signal?: AbortSignal) => api<ApplicantScoreWeights>('/api/applicants/score-weights', { signal }),

  saveScoreWeights: (request: ApplicantScoreWeightsRequest) =>
    api<ApplicantScoreWeights>('/api/applicants/score-weights', { method: 'PUT', body: request }),

  /** Schedules the Initial Interview — a qualified applicant only. */
  scheduleInterview: (id: string, request: ApplicantInterviewRequest) =>
    api<ApplicantListItem>(`/api/applicants/${id}/interview`, { method: 'PUT', body: request }),

  /** Soft — the identity is left alone, so a restore finds the name. */
  remove: (id: string) => api<void>(`/api/applicants/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/applicants/${id}/restore`, { method: 'POST' }),

  /**
   * Display names for a page of applicants — the applicant-scoped twin of `/api/persons/resolve`.
   * Refused whole if any id is not an applicant's; at most 100.
   */
  names: (personIds: string[], signal?: AbortSignal) =>
    api<PersonSummary[]>('/api/applicants/names', { method: 'POST', body: { personIds }, signal }),

  /** The name's parts for the edit form. Audited — call it on OPEN, never on mount. */
  name: (id: string, signal?: AbortSignal) => api<ApplicantName>(`/api/applicants/${id}/name`, { signal }),
};

const REQUIREMENTS = '/api/applicant-requirements';

/** What an applicant is expected to submit — Recruitment › Settings › Applicant Requirements. */
export const applicantRequirementsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<ApplicantRequirementListItem>>(`${REQUIREMENTS}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<ApplicantRequirementListItem>(`${REQUIREMENTS}/${id}`, { signal }),

  create: (body: ApplicantRequirementRequest) =>
    api<ApplicantRequirementListItem>(REQUIREMENTS, { method: 'POST', body }),

  update: (id: string, body: ApplicantRequirementRequest) =>
    api<ApplicantRequirementListItem>(`${REQUIREMENTS}/${id}`, { method: 'PUT', body }),

  /** Soft — ticks already recorded keep naming it. */
  remove: (id: string) => api<void>(`${REQUIREMENTS}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${REQUIREMENTS}/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>(`${REQUIREMENTS}/clear-data`, { method: 'POST' }),
};
